import { useMemo } from "react";
import { useLocation } from "react-router-dom";

import SwapHorizOutlinedIcon from "@mui/icons-material/SwapHorizOutlined";

import { useAppSelector } from "app/hooks";
import useSwitchPractice from "features/app-layout/layout/layout-top-bar/practice-selector/use-switch-practice.hook";
import { switchReturnPath } from "features/app-layout/layout/practice-context-banner/switch-return-path.util";
import { selectCurrentPractice } from "features/auth/auth-slice";
import useDebounce from "hooks/use-debounce.hook";
import useUserRoles from "hooks/use-user-roles.hook";
import { useGetAccessiblePracticesQuery } from "services/practices";
import type { AccessiblePractice } from "services/types/practices.type";

import type { QuickLauncherAction } from "./quick-launcher.types";

/**
 * A practice name needs two characters before the search narrows anything.
 * A practice id is searched from its first digit, since ids can be one digit long.
 */
export const MIN_PRACTICE_NAME_SEARCH_LENGTH = 2;

const PRACTICE_RESULT_LIMIT = 5;

const isSearchable = (query: string): boolean =>
  /^\d+$/.test(query) || query.length >= MIN_PRACTICE_NAME_SEARCH_LENGTH;

const describePractice = (
  practice: AccessiblePractice,
  currentPracticeId: number | undefined
): string =>
  [
    `#${practice.id}`,
    practice.is_current || practice.id === currentPracticeId
      ? "Current practice"
      : "Switch to this practice",
    practice.is_home ? "Home" : null
  ]
    .filter(Boolean)
    .join(" · ");

/**
 * Practice rows for the quick launcher. They do the same thing as the practice
 * switcher in the context banner, so a Suno user can type a practice name or id
 * after ⌘K instead of opening that menu.
 *
 * Suno staff only. The banner's switcher is an internal support tool (see the
 * `sunoOnly` note in `use-quick-launcher-actions.hook.ts`). The rows skip that
 * hook's filter, so the gate is applied here.
 *
 * Like patient rows, these are server-matched and must not go through
 * `filterActions`. The backend matches the numeric id as well as the name, and
 * a client-side filter on the label would drop every row found by id.
 */
export default function useQuickLauncherPractices(
  query: string,
  enabled: boolean
): { practiceActions: QuickLauncherAction[]; isSearching: boolean } {
  const { isSunoAdmin } = useUserRoles();
  const currentPractice = useAppSelector(selectCurrentPractice);
  const { switchToPractice } = useSwitchPractice();
  const { pathname } = useLocation();

  // 250ms, the same window the banner's switcher uses.
  const { value: debouncedQuery, isDebouncing } = useDebounce(
    query.trim(),
    250
  );

  const isActive = enabled && isSunoAdmin && isSearchable(debouncedQuery);

  const { currentData: practices, isFetching } = useGetAccessiblePracticesQuery(
    { search: debouncedQuery, limit: PRACTICE_RESULT_LIMIT },
    { skip: !isActive }
  );

  const practiceActions = useMemo(() => {
    if (!isActive || !practices) return [];
    return practices.slice(0, PRACTICE_RESULT_LIMIT).map((practice) => ({
      id: `practice:${practice.id}`,
      // One name for every practice row, or the adoption metric fans out per practice.
      telemetryId: "practice:switch",
      sunoOnly: true,
      label: practice.name,
      description: describePractice(practice, currentPractice?.id),
      group: "Practices" as const,
      Icon: SwapHorizOutlinedIcon,
      perform: () => {
        switchToPractice(practice.id, currentPractice?.id, {
          returnTo: switchReturnPath(pathname) ?? undefined
        });
      }
    }));
  }, [isActive, practices, currentPractice?.id, switchToPractice, pathname]);

  return {
    practiceActions,
    isSearching:
      enabled &&
      isSunoAdmin &&
      isSearchable(query.trim()) &&
      (isFetching || isDebouncing)
  };
}
